import { useState } from "react";
import { ConfigProvider, DatePicker } from "antd";
import { CalendarOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useIsDarkTheme } from "../../../store/hooks";
import { addDays, parseDateKey } from "../logic/dayTime";
import { useReservationFormat } from "../hooks/useReservationFormat";
import { useDatePickerLocale } from "../hooks/useDatePickerLocale";
import { useEscapeToClose } from "../hooks/useEscapeToClose";

const STEP_BUTTON =
  "flex h-full w-9 shrink-0 cursor-pointer items-center justify-center border-none bg-transparent text-[12px] text-body transition-colors duration-150 hover:bg-shade-1 hover:text-ink";

const DAY_MS = 86_400_000;

/**
 * ‹ „Днес, 25.09" › — смяна на деня; кликът върху датата отваря календар.
 * Друг ден: „Утре, 26.09" / „Вчера, 24.09" / „Пт, 03.10". 36px — колкото
 * „Нова резервация" и табовете на регионите; `fill` — разтяга се по ширината
 * (над списъка), датата е в средата.
 */
const DayNavigator = ({
  dateKey,
  todayKey,
  compact,
  fill = false,
  onChange,
}: {
  dateKey: string;
  todayKey: string;
  // Тесен екран: само датата, без иконата.
  compact: boolean;
  fill?: boolean;
  onChange: (dateKey: string) => void;
}) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  const isDark = useIsDarkTheme();
  const pickerLocale = useDatePickerLocale();
  const [pickerOpen, setPickerOpen] = useState(false);
  // Esc при отворен календар затваря само него (не и поповъра/„назад").
  useEscapeToClose(pickerOpen, () => setPickerOpen(false));

  const offset = Math.round(
    (parseDateKey(dateKey).getTime() - parseDateKey(todayKey).getTime()) / DAY_MS,
  );
  const date = format.shortDate(dateKey);
  const label =
    offset === 0
      ? t("reservations.day.today", { date })
      : offset === 1
        ? t("reservations.day.tomorrow", { date })
        : offset === -1
          ? t("reservations.day.yesterday", { date })
          : `${format.shortWeekday(dateKey)}, ${date}`;

  return (
    <div className={`flex h-9 items-stretch ${fill ? "min-w-0 flex-1" : "shrink-0"} overflow-hidden rounded-[10px] border border-shade-3 bg-surface`}>
      <button
        type="button"
        className={STEP_BUTTON}
        title={t("reservations.day.previous")}
        aria-label={t("reservations.day.previous")}
        onClick={() => onChange(addDays(dateKey, -1))}
      >
        <LeftOutlined />
      </button>
      <div className={`relative flex border-x border-shade-2 ${fill ? "min-w-0 flex-1" : ""}`}>
        <button
          type="button"
          title={t("reservations.day.pick")}
          onClick={() => setPickerOpen(true)}
          className={`flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap border-none bg-transparent text-[13px] ${fill ? "flex-1" : ""} font-semibold tabular-nums text-ink transition-colors duration-150 hover:bg-shade-1 ${
            compact ? "px-2.5" : "px-3.5"
          }`}
        >
          {!compact && <CalendarOutlined className="text-[14px] text-body" />}
          {label}
        </button>
        {/* Невидимото поле само закотвя календара под бутона. */}
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: "#D97706",
              // Token математиката на antd не разбира var() → условна стойност.
              controlItemBgActive: isDark ? "#382a12" : "#FFF4E0",
            },
          }}
        >
          <DatePicker
            locale={pickerLocale}
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            value={dayjs(parseDateKey(dateKey))}
            allowClear={false}
            placement="bottomRight"
            onChange={(value) => {
              setPickerOpen(false);
              if (value) onChange(value.format("YYYY-MM-DD"));
            }}
            className="!pointer-events-none !absolute !inset-0 !opacity-0"
            tabIndex={-1}
            aria-hidden
          />
        </ConfigProvider>
      </div>
      <button
        type="button"
        className={STEP_BUTTON}
        title={t("reservations.day.next")}
        aria-label={t("reservations.day.next")}
        onClick={() => onChange(addDays(dateKey, 1))}
      >
        <RightOutlined />
      </button>
    </div>
  );
};

export default DayNavigator;
